import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useContent } from "../lib/useContent";

export default function Contact() {
    const { contact } = useContent();
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please complete the required fields.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch('/api/contact', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify(form),
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                const msg = data?.error?.message ?? 'Something went wrong.';
                toast.error(msg);
                setSubmitting(false);
                return;
            }
            setSuccess(true);
            toast.success("Message sent — thank you.");
            setForm({ name: "", email: "", subject: "", message: "", website: "" });
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page" className="pt-32 md:pt-40">
            <section className="px-6 md:px-12 lg:px-24">
                <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1 }}
                        className="lg:col-span-5"
                    >
                        <p className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-accent">
                            04 — Contact
                        </p>
                        <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tighter text-aplum mt-4">
                            Get in <em className="italic">touch</em>.
                        </h1>
                        <p className="font-sans-display text-base text-aplum-dim mt-8 leading-relaxed max-w-md">
                            For exhibition enquiries, commissions, press, and studio visits — please
                            reach out below or via email. Replies typically within a week.
                        </p>

                        <div className="mt-16 space-y-8">
                            <div>
                                <p className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-dim mb-2">Email</p>
                                <a
                                    href={`mailto:${contact.email}`}
                                    data-testid="contact-email-link"
                                    className="font-serif-display text-2xl text-aplum hover:text-aplum-accent transition-colors"
                                >
                                    {contact.email}
                                </a>
                            </div>
                            <div>
                                <p className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-dim mb-2">Instagram</p>
                                <a
                                    href={contact.instagram.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="contact-instagram-link"
                                    className="font-serif-display text-2xl text-aplum hover:text-aplum-accent transition-colors"
                                >
                                    {contact.instagram.handle}
                                </a>
                            </div>
                            <div>
                                <p className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-dim mb-2">Studio</p>
                                <p className="font-serif-display text-lg text-aplum">
                                    {contact.studio}
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    <motion.form
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.2 }}
                        onSubmit={onSubmit}
                        data-testid="contact-form"
                        className="lg:col-span-7 space-y-10"
                    >
                        {/* honeypot: off-screen, not display:none so browsers will autofill if bot-driven */}
                        <input
                            type="text"
                            name="website"
                            value={form.website ?? ""}
                            onChange={onChange}
                            autoComplete="off"
                            tabIndex={-1}
                            aria-hidden="true"
                            style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0 }}
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <label className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-dim">Name *</label>
                                <input
                                    name="name"
                                    value={form.name}
                                    onChange={onChange}
                                    required
                                    data-testid="contact-input-name"
                                    className="flush-input mt-2"
                                    placeholder="Your name"
                                />
                            </div>
                            <div>
                                <label className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-dim">Email *</label>
                                <input
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={onChange}
                                    required
                                    data-testid="contact-input-email"
                                    className="flush-input mt-2"
                                    placeholder="you@email.com"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-dim">Subject</label>
                            <input
                                name="subject"
                                value={form.subject}
                                onChange={onChange}
                                data-testid="contact-input-subject"
                                className="flush-input mt-2"
                                placeholder="Exhibition enquiry / commission / press"
                            />
                        </div>
                        <div>
                            <label className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-dim">Message *</label>
                            <textarea
                                name="message"
                                value={form.message}
                                onChange={onChange}
                                required
                                rows={6}
                                data-testid="contact-input-message"
                                className="flush-input mt-2 resize-none"
                                placeholder="Tell me a little about what you have in mind…"
                            />
                        </div>

                        <div className="flex items-center justify-between pt-4">
                            <p className="font-sans-display text-[11px] uppercase tracking-widest-x text-aplum-dim">
                                {success ? "Message received. Thank you." : "We respect your inbox."}
                            </p>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="contact-submit"
                                className="font-sans-display text-xs uppercase tracking-widest-x text-aplum border border-aplum px-8 py-4 hover:bg-aplum-accent hover:border-aplum-accent transition-all duration-500 disabled:opacity-50"
                            >
                                {submitting ? "Sending…" : "Send Message →"}
                            </button>
                        </div>
                    </motion.form>
                </div>
            </section>
        </div>
    );
}
